/**
 * Agregações financeiras do Dashboard — fonte única de verdade.
 *
 * Regras (definições objetivas):
 * - Emprestado no período: soma do valor dos empréstimos criados no período.
 * - Recebido no período: capital + juros + multas efetivamente recebidos.
 * - Capital devolvido: só conta quando o dinheiro voltou de fato —
 *   quitações (is_settlement) e parcelas de empréstimos de Valor Fixo.
 *   Em juros Mensal/Diário, a renovação grava `principal_amount` por
 *   histórico, mas o capital continua na rua: não entra como recebido.
 * - Lucro real: juros + multas. Nunca inclui capital.
 * - Fluxo líquido: recebido − emprestado.
 * - Na rua: Valor Fixo → parcelas não pagas; Mensal/Diário → principal
 *   original menos capital já devolvido.
 */

import { remainingBalance, round2 } from "@/lib/installments";

export interface FinanceLoan {
  id: string;
  clientId?: string;
  amount: number;
  loanDate: Date;
  status: "paid" | "active" | "overdue";
  interestType?: "monthly" | "fixed" | "daily";
  installments?: number;
  paidInstallments?: number;
  installmentValues?: unknown;
}

export interface FinancePayment {
  id: string;
  loanId: string;
  amount: number;
  interestAmount: number;
  principalAmount: number;
  penaltyAmount: number;
  paidAt: Date;
  isSettlement?: boolean;
}

export interface Totals {
  /** Caixa recebido: capital + juros + multas. */
  received: number;
  /** Capital que voltou para o bolso. */
  principal: number;
  interest: number;
  penalty: number;
  /** Lucro real: juros + multas. */
  profit: number;
}

const emptyTotals = (): Totals => ({ received: 0, principal: 0, interest: 0, penalty: 0, profit: 0 });

/** Decompõe um pagamento no que realmente entrou em caixa. */
export function decomposePayment(
  payment: FinancePayment,
  loan?: FinanceLoan,
): { principal: number; interest: number; penalty: number; received: number } {
  const interest = Number(payment.interestAmount) || 0;
  const penalty = Number(payment.penaltyAmount) || 0;
  const rawPrincipal = Number(payment.principalAmount) || 0;

  const isSettlement = Boolean(payment.isSettlement);
  const isFixed = loan?.interestType === "fixed";
  const principal = isSettlement || isFixed ? rawPrincipal : 0;

  return {
    principal: round2(principal),
    interest: round2(interest),
    penalty: round2(penalty),
    received: round2(principal + interest + penalty),
  };
}

const inRange = (d: Date, start?: Date, end?: Date) =>
  (!start || d >= start) && (!end || d <= end);

/** Soma os pagamentos de um intervalo (datas opcionais = tudo). */
export function aggregatePayments(
  payments: FinancePayment[],
  loans: FinanceLoan[],
  start?: Date,
  end?: Date,
): Totals {
  const byId = new Map(loans.map((l) => [l.id, l]));
  const totals = emptyTotals();

  for (const p of payments) {
    if (!p.paidAt || !inRange(p.paidAt, start, end)) continue;
    const d = decomposePayment(p, byId.get(p.loanId));
    totals.principal += d.principal;
    totals.interest += d.interest;
    totals.penalty += d.penalty;
    totals.received += d.received;
  }

  totals.principal = round2(totals.principal);
  totals.interest = round2(totals.interest);
  totals.penalty = round2(totals.penalty);
  totals.received = round2(totals.received);
  totals.profit = round2(totals.interest + totals.penalty);
  return totals;
}

/** Dinheiro colocado na rua no intervalo. */
export function loanedInRange(loans: FinanceLoan[], start?: Date, end?: Date): number {
  let total = 0;
  for (const l of loans) {
    if (!l.loanDate || !inRange(l.loanDate, start, end)) continue;
    total += Number(l.amount) || 0;
  }
  return round2(total);
}

/** Saldo em aberto de um empréstimo hoje. */
export function outstandingBalance(loan: FinanceLoan, payments: FinancePayment[]): number {
  if (loan.status === "paid") return 0;

  if (loan.interestType === "fixed") {
    return remainingBalance(
      {
        amount: Number(loan.amount) || 0,
        installments: Number(loan.installments) || 1,
        installmentValues: loan.installmentValues ?? null,
      },
      Number(loan.paidInstallments) || 0,
    );
  }

  const repaid = payments
    .filter((p) => p.loanId === loan.id)
    .reduce((sum, p) => sum + decomposePayment(p, loan).principal, 0);

  return round2(Math.max(0, (Number(loan.amount) || 0) - repaid));
}

/** Total na rua hoje (todos os empréstimos em aberto). */
export function totalOutstanding(loans: FinanceLoan[], payments: FinancePayment[]): number {
  return round2(loans.reduce((sum, l) => sum + outstandingBalance(l, payments), 0));
}

export interface MonthPoint {
  key: string;
  month: string;
  label: string;
  loaned: number;
  principal: number;
  profit: number;
  interest: number;
  penalty: number;
  received: number;
  net: number;
  /** Saldo na rua no fim do mês. */
  outstanding: number;
  /** Lucro acumulado até o fim do mês. */
  cumulativeProfit: number;
}

const MONTH_NAMES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** Série mensal dos últimos `months` meses (inclui o mês atual). */
export function monthlySeries(
  loans: FinanceLoan[],
  payments: FinancePayment[],
  months: number,
): MonthPoint[] {
  const byId = new Map(loans.map((l) => [l.id, l]));
  const today = new Date();
  const points: MonthPoint[] = [];

  for (let i = months - 1; i >= 0; i--) {
    const start = new Date(today.getFullYear(), today.getMonth() - i, 1, 0, 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth() - i + 1, 0, 23, 59, 59, 999);
    const t = aggregatePayments(payments, loans, start, end);
    const loaned = loanedInRange(loans, start, end);

    // Saldo na rua no fim do mês: principal emprestado até a data,
    // menos o capital devolvido até a data.
    let outstanding = 0;
    for (const l of loans) {
      if (!l.loanDate || l.loanDate > end) continue;
      const repaid = payments
        .filter((p) => p.loanId === l.id && p.paidAt && p.paidAt <= end)
        .reduce((sum, p) => sum + decomposePayment(p, byId.get(p.loanId)).principal, 0);
      outstanding += Math.max(0, (Number(l.amount) || 0) - repaid);
    }

    points.push({
      key: `${start.getFullYear()}-${start.getMonth()}`,
      month: MONTH_NAMES[start.getMonth()],
      label: `${MONTH_NAMES[start.getMonth()]}/${String(start.getFullYear()).slice(2)}`,
      loaned,
      principal: t.principal,
      profit: t.profit,
      interest: t.interest,
      penalty: t.penalty,
      received: t.received,
      net: round2(t.received - loaned),
      outstanding: round2(outstanding),
      cumulativeProfit: 0,
    });
  }

  let acc = 0;
  for (const p of points) {
    acc = round2(acc + p.profit);
    p.cumulativeProfit = acc;
  }

  return points;
}

export type PeriodKey = "month" | "3m" | "6m" | "12m" | "all";

export const PERIOD_OPTIONS: Array<{ key: PeriodKey; label: string; months: number }> = [
  { key: "month", label: "Este mês", months: 1 },
  { key: "3m", label: "3 meses", months: 3 },
  { key: "6m", label: "6 meses", months: 6 },
  { key: "12m", label: "12 meses", months: 12 },
  { key: "all", label: "Tudo", months: 60 },
];

/** Intervalo de datas do período escolhido. */
export function periodRange(period: PeriodKey): { start?: Date; end: Date; months: number } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  const months = PERIOD_OPTIONS.find((p) => p.key === period)?.months ?? 1;
  if (period === "all") return { start: undefined, end, months };
  const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1, 0, 0, 0, 0);
  return { start, end, months };
}
